'use client'
import {
  Text,
  Button,
  HStack,
  Input,
  useToast,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useDeleteAccount_ADMIN, useGetAllUsers } from "queries";
import Link from "next/link";
import { fetch } from "libs";
import UploadModel from "./UploadModel";
import OptionCategoryPanel from "./OptionCategoryPanel";
import StylePanel from "./StylePanel";
import { useQueryClient } from "@tanstack/react-query";
import { POST_ADD_CREDIT, POST_BLOCK_USER } from "api";
import ModelShowcaseUpload from "./ModelShowcaseUpload";
import NewTransactionsPage from "./NewTransactionsPage";
import { SimpleTable, RemixGalleryLayout } from "components";
import { RemixGalleryImage } from "types";
import  LoraUpload  from "./LoraUpload";
import InstanceStatesPanel from "./InstanceStatesPanel";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ErrorLogsPanel from "./ErrorLogsPanel";

export const AdminPage = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState("");
  const [endDate, setEndDate] = useState(new Date());
  const [startDate, setStartDate] = useState(() => {
    const newDate = new Date(endDate);
    newDate.setDate(newDate.getDate() - 1);
    return newDate;
  });

  const [activeTab, setActiveTab] = useState<number>(() => {
    // Retrieve the saved active tab index from localStorage, default to 0 if not found
    return parseInt(localStorage.getItem("activeTab") || "0");
  });

  const handleTabChange = (index: number) => {
    setActiveTab(index);
    localStorage.setItem("activeTab", index.toString());
  };


    // Function to format the date to the start of the day in UTC (YYYY-MM-DD)
    const formatDateToUTCStart = (date: Date) => {
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())).toISOString().split('T')[0];
    };
  
    // Function to format the date to the end of the day in UTC (YYYY-MM-DDTHH:MM:SS)
    const formatDateToUTCEnd = (date: Date) => {
      return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 23, 59, 59)).toISOString();
    };
  
    const { data, isLoading, refetch } = useGetAllUsers({ 
      page, 
      pageSize, 
      search,
      start_date: formatDateToUTCStart(startDate),  // Pass date in the correct UTC format (start of the day)
      end_date: formatDateToUTCEnd(endDate),    // Ensure date is passed in the correct format (end of the day)
    });
  const totalPages = data ? Math.ceil(data.total_users / pageSize) : 1;
  const [userCredit, setUserCredit] = useState<Record<string, number>>({});

  const {
    isOpen: isRemixGalleryOpen,
    onOpen: onRemixGalleryOpen,
    onClose: onRemixGalleryClose,
  } = useDisclosure();

  const [selectedRemixGalleryImage, setSelectedRemixGalleryImage] =
    useState<RemixGalleryImage | null>(null);

  const toast = useToast();
  const deleteAccount = useDeleteAccount_ADMIN();

  const handleCreditUpdate = async (user: any, isAdd: boolean) => {
    const creditAmount = Number(userCredit[user.email]);

    if (!creditAmount || creditAmount === 0) {
      toast({
        title: "Invalid credit amount",
        description: "Please enter a valid non-zero credit amount.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const finalCreditAmount = isAdd ? creditAmount : -creditAmount;

    const url = POST_ADD_CREDIT(user.id, finalCreditAmount);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (Object.keys(response).length === 0) {
        throw new Error("Network response was not ok");
      }

      queryClient.invalidateQueries({
        queryKey: ["users", { page, pageSize, search, start_date: formatDateToUTCStart(startDate), end_date: formatDateToUTCEnd(endDate) }],
      });

      toast({
        title: "Success",
        description: `Credit ${isAdd ? "added" : "deducted"} successfully`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error(`Error ${isAdd ? "adding" : "deducting"} credit:`, error);

      toast({
        title: "Error",
        description: String(
          (error as any).detail
            ? typeof (error as any).detail === "object"
              ? JSON.stringify((error as any).detail)
              : (error as any).detail
            : error
        ),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteUser = async (user: any) => {
    const confirmation = window.confirm(`Are you sure you want to delete the account for ${user.email}? This action cannot be undone.`);
  
    if (!confirmation) {
      return; // If the admin clicks "Cancel", don't proceed with the deletion
    }
  
    // Deduct the user's credit to zero
    const currentCredit = user.credit ?? 0;
    if (currentCredit > 0) {
      try {
        const url = POST_ADD_CREDIT(user.id, -currentCredit);
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        });
  
        if (Object.keys(response).length === 0) {
          throw new Error("Network response was not ok");
        }
  
        queryClient.invalidateQueries({
          queryKey: ["users", { page, pageSize, search, start_date: formatDateToUTCStart(startDate), end_date: formatDateToUTCEnd(endDate) }],
        });
  
        toast({
          title: "Success",
          description: `Credit deducted to zero successfully`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (error) {
        console.error("Error deducting credit to zero:", error);
  
        toast({
          title: "Error",
          description: String(
            (error as any).detail
              ? typeof (error as any).detail === "object"
                ? JSON.stringify((error as any).detail)
                : (error as any).detail
              : error
          ),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
  
        return; // Stop further execution if credit deduction fails
      }
    }
  
    // Proceed with user deletion
    deleteAccount.mutate(user.id, {
      onSuccess: () => {
        toast({
          title: "User Deleted",
          description: `User with email ${user.email} and their associated models have been deleted.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      },
      onError: (error) => {
        console.error("Error deleting user:", error);
  
        toast({
          title: "Error",
          description: String(error),
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      },
    });
  };

  const handleBlockUser = async (user: any, block: boolean) => {
    const action = block ? "block" : "unblock";
    const confirmation = window.confirm(`Are you sure you want to ${action} the user with email ${user.email}?`);
  
    if (!confirmation) {
      return; // If the admin clicks "Cancel", don't proceed with blocking/unblocking
    }
  
    try {
      const url = POST_BLOCK_USER(user.email, block);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });
  
      if (response.ok) {
        toast({
          title: "Success",
          description: `User ${block ? "blocked" : "unblocked"} successfully.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
  
        queryClient.invalidateQueries({
          queryKey: ["users", { page, pageSize, search, start_date: formatDateToUTCStart(startDate), end_date: formatDateToUTCEnd(endDate) }],
        });
      } else {
        throw new Error();
      }
    } catch {
      toast({
        title: "Success",
        description: "User has been blocked.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    }
  };
   

  const columns = [
    {
      title: <Text minW="250px">Email</Text>,
      align: "start",
      format: (user: any) => (
        <Link href={`/admin/${user._id}`}>
          <Text minW="250px">{user.email}</Text>
        </Link>
      ),
    },
    {
      title: "Balance",
      align: "start",
      format: (user: any) => (
        <HStack spacing="12px">
          <Text w="80px">{(user.credit ?? 0)?.toFixed(2)}$</Text>
          <Input
            w="200px"
            placeholder="Enter credit amount"
            onChange={(event) =>
              setUserCredit({
                ...userCredit,
                [user.email]: event.target.value,
              })
            }
          />
          <Button colorScheme="blue" onClick={() => handleCreditUpdate(user, true)}>Add</Button>
          <Button colorScheme="red" onClick={() => handleCreditUpdate(user, false)}>Deduct</Button>
          <Button colorScheme="red" variant="outline" onClick={() => handleDeleteUser(user)}>
            Delete User
          </Button>
          <Button colorScheme="yellow" variant="outline" onClick={() => handleBlockUser(user, true)}>
            Block User
          </Button>
        </HStack>
      ),
    },
    {
      title: "User registered at",
      align: "end",
      format: (user: any) => (
        <Text w="100%" textAlign="end">
          {new Date(user.created_at).toLocaleString()}
        </Text>
      ),
    },
  ];

  const templateColumns = "1fr 2fr 1fr";

  useEffect(() => {
    refetch();
  }, [search, startDate, endDate]);

  return (
    <Tabs index={activeTab} onChange={handleTabChange} isLazy variant="enclosed">
      <TabList mb="1em">
        <Tab>User Management</Tab>
        <Tab>Transactions</Tab>
        <Tab>Uploads</Tab>
        <Tab>Option Categories</Tab>
        <Tab>Styles</Tab>
        <Tab>Model Showcase</Tab>
        <Tab>Lora Upload</Tab>
        <Tab>Instance States</Tab>
        <Tab>Generations Errors</Tab>
        <Button
          bg="transparent"
          color="white"
          _hover={{}}
          _active={{}}
          fontWeight="normal"
          alignSelf="center"
          onClick={onRemixGalleryOpen}
        >
          Remix Gallery
        </Button>
        <RemixGalleryLayout
          isAdmin={true}
          isOpen={isRemixGalleryOpen}
          onClose={onRemixGalleryClose}
          selectedImage={selectedRemixGalleryImage}
          setSelectedImage={setSelectedRemixGalleryImage}
        />
      </TabList>
      <TabPanels>
        <TabPanel>
          <HStack>
          <Input
            placeholder="Search by email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}  // Correctly update search state
          />

            <Text>From:</Text>
            <DatePicker
              customInput={<Input />}
              selected={startDate}
              onChange={(date) => setStartDate(date!)}
            />
            <Text>To:</Text>
            <DatePicker
              customInput={<Input />}
              selected={endDate}
              onChange={(date) => setEndDate(date!)}
            />
          </HStack>
          <HStack spacing="24px">
            <Button onClick={() => setPage(1)}>First page</Button>
            <Button onClick={() => setPage((old) => Math.max(old - 1, 1))}>
              Previous page
            </Button>
            <Text>Page: {page}</Text>
            <Button onClick={() => setPage((old) => old + 1)}>Next page</Button>
            <Button onClick={() => setPage(totalPages)}>Last page</Button>
          </HStack>
          <SimpleTable
            columns={columns}
            data={data ? [...data.users].reverse() : []}
            isLoading={isLoading}
            templateColumns={templateColumns}
          />
        </TabPanel>
        <TabPanel>
          <NewTransactionsPage />
        </TabPanel>
        <TabPanel>
          <UploadModel />
        </TabPanel>
        <TabPanel>
          <OptionCategoryPanel />
        </TabPanel>
        <TabPanel>
          <StylePanel />
        </TabPanel>
        <TabPanel>
          <ModelShowcaseUpload />
        </TabPanel>
        <TabPanel>
          <LoraUpload />
        </TabPanel>
        <TabPanel>
          <InstanceStatesPanel />
        </TabPanel>
        <TabPanel>
          <ErrorLogsPanel/>
          </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default AdminPage;
